/**
 * `window.__game` (like NIGHTDROP's): the session, the view and the music for checks, scene-setting
 * for captures, `advance` to run frames without waiting for real time, and `save` for exact frames.
 */

import { placeBubble, popBubble } from '../../sim/bubbles';
import { applyPowerup } from '../../sim/items/effects';
import type { Command, PlayerId } from '../../sim/types';
import type { GameView } from '../../render/game/GameView';
import type { SettingsStore } from '../../settings';
import { frameDataUrl } from '../devKit';
import type { FrontEnd } from './front';
import { hudState, sessionBanners } from './hudState';
import type { MusicDirector } from './music';
import type { Session } from './session';

export function exposeGame(
  session: Session, view: GameView, music: MusicDirector, front: FrontEnd, settings: SettingsStore,
): void {
  const draw = (): void => {
    view.setTransition(session.transition.cover(1));
    view.render(session.world, session.prev, 1, performance.now() / 1000, hudState(session),
      sessionBanners(session, settings.get().display.reduceFlashing, front.menus.covering), (ctx) => front.draw(ctx));
  };
  Object.assign(window, {
    __game: {
      session,
      view,
      music,
      menus: front.menus,
      settings,
      /** Scene-setting for captures and checks: a bubble placed or burst, a power-up handed out. */
      debug: {
        placeBubble: (spec: Parameters<typeof placeBubble>[1]) => (session.world ? placeBubble(session.world, spec) : null),
        popBubble: (id: number, by: PlayerId | -1 = 0) => session.world && popBubble(session.world, id, by),
        powerup: (index: number, id: PlayerId = 0) => {
          const w = session.world;
          if (w?.itemSystem) applyPowerup(w, w.itemSystem, index, id);
        },
      },
      /**
       * Run `frames` sim frames with fixed controls and draw, without waiting for real time (tests
       * and captures; pause the game first so the loop does not run too).
       */
      advance(frames: number, p1: Partial<Command> = {}, p2: Partial<Command> = {}): void {
        const idle = { left: false, right: false, jump: false, fire: false };
        for (let i = 0; i < frames; i++) session.tick([{ ...idle, ...p1 }, { ...idle, ...p2 }], true);
        draw();
      },
      /** Save the current frame to docs/lookdev/<name>.png (dev server only), as `__look.save` does. */
      async save(name: string): Promise<string> {
        draw();
        const res = await fetch(`/__lookdev/save?name=${encodeURIComponent(name)}`, {
          method: 'POST', body: frameDataUrl(view.pipeline, view.hud.canvas),
        });
        return res.text();
      },
    },
  });
}
